import React, { useEffect } from 'react';
import { AiFillHeart, AiOutlineHeart, AiOutlineShoppingCart } from 'react-icons/ai';
import { FaFemale } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Ratings from '../Ratings';
import {
  add_to_card,
  add_to_wishlist,
  remove_wishlist,
  get_wishlist_products,
  messageClear,
} from '../../store/reducers/cardReducer';
import {
  add_viewed_product,
  add_cart_activity,
  add_wishlist_activity,
  add_swap_activity
} from '../../store/reducers/homeReducer';
import { base_url } from '../../utils/config';

const getAverageRating = (product) => {
  if (!product?.rating_review?.length) return 0;
  let total = 0, sum = 0;
  product.rating_review.forEach((rev, i) => {
    const c = rev.sum || 0, val = 5 - i;
    total += c; sum += c * val;
  });
  return total ? (sum / total).toFixed(1) : 0;
};

const ShopProducts = ({ styles, products }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userInfo } = useSelector(s => s.auth);
  const { wishlist, successMessage, errorMessage } = useSelector(s => s.card);

  // load wishlist
  useEffect(() => {
    if (userInfo) dispatch(get_wishlist_products(userInfo.id));
  }, [dispatch, userInfo]);

  // toasts & refresh
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      if (userInfo) dispatch(get_wishlist_products(userInfo.id));
      dispatch(messageClear());
    }
    if (errorMessage) {
      toast.error(errorMessage);
      dispatch(messageClear());
    }
  }, [successMessage, errorMessage, dispatch, userInfo]);

  const handleAddCard = (id) => {
    if (userInfo) {
      dispatch(add_to_card({ userId: userInfo.id, quantity: 1, productId: id }));
      // Track cart activity (removed duplicate trackAddToCart call)
      dispatch(add_cart_activity({ userId: userInfo.id, productId: id, source: 'ShopProducts' }));
    } else {
      navigate('/login');
    }
  };

  const toggleWishlist = (p, e) => {
    e.stopPropagation();
    if (!userInfo) return navigate('/login');
    const exists = wishlist.find(w => w.productId === p._id);
    if (exists) {
      dispatch(remove_wishlist(exists._id));
    } else {
      dispatch(add_to_wishlist({
        userId:    userInfo.id,
        productId: p._id,
        name:      p.name,
        price:     p.price,
        image:     p.images[0],
        discount:  p.discount,
        slug:      p.slug,
      }));
      // Track wishlist activity
      dispatch(add_wishlist_activity({ userId: userInfo.id, productId: p._id, source: 'ShopProducts' }));
    }
  };

  const handleSwap = (p, e) => {
    e.stopPropagation();
    if (!userInfo) return navigate('/login');
    // Track swap activity
    dispatch(add_swap_activity({ userId: userInfo.id, productId: p._id, source: 'ShopProducts' }));
  };

  const handleProductClick = (productId) => {
    navigate(`/product/details/${productId}`);
    // View tracking is handled in Details page to avoid duplicates
  };

  if (!products?.length) {
    return <div className="text-center p-4 text-gray-500">No products found.</div>;
  }

  // GRID CARD
  const renderGridCard = (p) => {
    const avg = getAverageRating(p);
    const discounted = p.discount
      ? p.price - Math.floor(p.price * p.discount / 100)
      : p.price;
    const inWish = wishlist.some(w => w.productId === p._id);

    return (
      <div
        key={p._id}
        onClick={() => handleProductClick(p._id)}
        className="bg-gray-100 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden flex flex-col"
      >
        {/* image + wishlist + percent badge */}
        <div className="relative aspect-[3.4/4] overflow-hidden">
          <img
            src={p.images[0]}
            alt={p.name}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          />
          <button
            onClick={e => { e.stopPropagation(); toggleWishlist(p, e); }}
            className="absolute top-2 left-2 p-1 rounded-full"
            style={{
              background: 'var(--color-card-bg)',
              border:     '1px solid var(--color-border)',
              color:      inWish ? 'var(--color-primary)' : 'var(--color-border)',
            }}
          >
            {inWish ? <AiFillHeart size={18}/> : <AiOutlineHeart size={18}/>}
          </button>
          {p.discount > 0 && (
            <div
              className="absolute bottom-2 left-2 px-2 py-1 bg-white bg-opacity-80 rounded text-xs font-semibold"
              style={{ color: 'var(--color-primary)' }}
            >
              -{p.discount}%
            </div>
          )}
        </div>

        {/* details */}
        <div className="p-3 flex flex-col flex-grow">
          <h2 className="text-sm font-semibold text-gray-800 line-clamp-1">{p.name}</h2>
          {p.shopName && <p className="text-xs text-gray-500">{p.shopName}</p>}
          <div className="mt-2 flex items-baseline gap-2">
            {p.discount > 0 ? (
              <>
                <span className="line-through text-sm text-gray-500">₹{p.price}</span>
                <span className="text-lg font-bold" style={{ color: 'var(--color-primary)' }}>
                  ₹{discounted}
                </span>
              </>
            ) : (
              <span className="text-lg font-bold text-gray-800">₹{p.price}</span>
            )}
          </div>
          <div className="flex items-center text-xs text-gray-600 mt-1">
            <Ratings ratings={parseFloat(avg)} />
            <span className="ml-1">({p.totalReview||0} reviews)</span>
          </div>
        </div>

        {/* actions */}
        <div className="flex border-t" style={{ borderColor: 'var(--color-border)' }}>
          <button
            onClick={e => { e.stopPropagation(); handleAddCard(p._id); }}
            className="button-primary btn-cart flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm rounded-bl-lg h-8"
          >
            <AiOutlineShoppingCart className="text-lg" /> Cart
          </button>
          <button
            onClick={e => handleSwap(p, e)}
            className="button-secondary btn-swap flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm rounded-br-lg"
          >
            <FaFemale className="text-lg" /> Swap
          </button>
        </div>
      </div>
    );
  };

  // LIST CARD
  const renderListCard = (p) => {
    const avg = getAverageRating(p);
    const discounted = p.discount
      ? p.price - Math.floor(p.price * p.discount / 100)
      : p.price;
    const inWish = wishlist.some(w => w.productId === p._id);

    return (
      <div
        key={p._id}
        onClick={() => handleProductClick(p._id)}
        className="bg-gray-100 w-full rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden flex mb-4"
      >
        {/* image + percent badge */}
        <div className="relative w-1/3 flex-none aspect-[3.4/4] overflow-hidden">
          <img
            src={p.images[0]}
            alt={p.name}
            className="w-full h-full object-cover"
          />
          <button
            onClick={e => { e.stopPropagation(); toggleWishlist(p, e); }}
            className="absolute top-2 left-2 p-1 rounded-full bg-white border border-gray-200 text-gray-500"
          >
            {inWish
              ? <AiFillHeart size={18} color="var(--color-primary)" />
              : <AiOutlineHeart size={18} />
            }
          </button>
          {p.discount > 0 && (
            <div className="absolute bottom-2 left-2 px-1 py-0.5 bg-white bg-opacity-80 rounded text-xs font-semibold" style={{ color: 'var(--color-primary)' }}>
              -{p.discount}%
            </div>
          )}
        </div>

        {/* content & description */}
        <div className="flex-1 p-4 flex flex-col justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-800 line-clamp-1">{p.name}</h3>
            {p.shopName && <p className="text-sm text-gray-500">{p.shopName}</p>}
            <div className="mt-2 flex items-baseline gap-2">
              {p.discount > 0 ? (
                <>
                  <span className="line-through text-sm text-gray-500">₹{p.price}</span>
                  <span className="text-lg font-bold" style={{ color: 'var(--color-primary)' }}>
                    ₹{discounted}
                  </span>
                </>
              ) : (
                <span className="text-lg font-bold text-gray-800">₹{p.price}</span>
              )}
            </div>
            <div className="flex items-center text-sm text-gray-600 mt-1">
              <Ratings ratings={parseFloat(avg)} />
              <span className="ml-2">({p.totalReview||0})</span>
            </div>
            {p.description && (
              <p className="mt-2 text-sm text-gray-700 line-clamp-2">
                {p.description}
              </p>
            )}
          </div>

          {/* smaller, rounded action buttons */}
          <div className="flex gap-3 mt-4 flex-col ">
            <button
              onClick={e => { e.stopPropagation(); handleAddCard(p._id); }}
              className="button-primary px-4 py-2 text-xs !rounded-sm flex items-center justify-center h-6 "
            >
              <AiOutlineShoppingCart className="text-base mr-1" /> Add to cart
            </button>
            <button
              onClick={e => handleSwap(p, e)} 
              className="button-secondary px-4 py-2 text-xs !rounded-sm flex items-center justify-center h-6 "
            >
              <FaFemale className="text-base mr-1" />Virtual Swap
            </button>
          </div>
        </div>
      </div>
    );
  };

  return styles === 'grid'
    ? (
      <div className="grid grid-cols-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {products.map(renderGridCard)}
      </div>
    ) : (
      <div className="flex flex-col">
        {products.map(renderListCard)}
      </div>
    );
};

export default ShopProducts;
